import { COMMUNITIES_ENDPOINTS } from "../../config/apiConfig";
import { createAuthAxios, handleApiError } from "../../utils/apiUtils";

/**
 * Service for Reddit-style community feed operations (Phase 2)
 */
const communityFeedService = {
  /**
   * Get unified feed from all communities
   * @param {Object} params - Feed parameters
   * @param {string} params.sort - Sorting algorithm: 'hot' | 'best' | 'top' | 'new' | 'rising' | 'controversial'
   * @param {string} params.filter - Feed filter: 'joined' | 'all'
   * @param {string} params.tags - Comma-separated tags (optional)
   * @param {string} params.time - Time period for top/controversial: 'hour' | 'day' | 'week' | 'month' | 'year' | 'all'
   * @param {number} params.limit - Number of threads per page (1-50, default: 20)
   * @param {string} params.cursor - Pagination cursor for next page
   * @returns {Promise<Object>} Feed data with threads and pagination info
   */
  getUnifiedFeed: async ({
    sort = "hot",
    filter = "joined",
    tags = "",
    time = "week",
    limit = 20,
    cursor = null,
  } = {}) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams();

      params.append("sort", sort);
      params.append("filter", filter);
      params.append("limit", limit.toString());

      if (tags) {
        params.append("tags", tags);
      }

      // Time filter only for top and controversial
      if ((sort === "top" || sort === "controversial") && time) {
        params.append("time", time);
      }

      if (cursor) {
        params.append("cursor", cursor);
      }

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.FEED}?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get popular feed (platform-wide)
   * @param {string} time - Time period: 'day' | 'week' | 'month'
   * @param {number} limit - Number of threads
   * @returns {Promise<Object>} Popular threads
   */
  getPopularFeed: async (time = "day", limit = 20) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams({
        time,
        limit: limit.toString(),
      });

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.FEED_POPULAR}?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get trending feed
   * @param {number} limit - Number of threads
   * @returns {Promise<Object>} Trending threads
   */
  getTrendingFeed: async (limit = 20) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams({
        limit: limit.toString(),
      });

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.FEED_TRENDING}?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get feed stats
   * @returns {Promise<Object>} Feed statistics
   */
  getFeedStats: async () => {
    try {
      const api = createAuthAxios();
      const response = await api.get(COMMUNITIES_ENDPOINTS.FEED_STATS);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Vote on thread (upvote/toggle)
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @returns {Promise<Object>} Vote response with updated counts
   */
  voteThread: async (communityId, threadId) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.VOTE_THREAD(communityId, threadId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Downvote on thread (downvote/toggle)
   * @param {string} communityId - Community ID
   * @param {string} threadId - Thread ID
   * @returns {Promise<Object>} Downvote response with updated counts
   */
  downvoteThread: async (communityId, threadId) => {
    try {
      const api = createAuthAxios();
      const response = await api.post(
        COMMUNITIES_ENDPOINTS.DOWNVOTE_THREAD(communityId, threadId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Save thread to collection
   * @param {string} threadId - Thread ID
   * @param {string} collection - Collection name (optional)
   * @param {Array<string>} tags - Tags for organization (optional)
   * @returns {Promise<Object>} Save response
   */
  saveThread: async (threadId, collection = null, tags = []) => {
    try {
      const api = createAuthAxios();
      const payload = {};

      if (collection) {
        payload.collection = collection;
      }

      if (tags && tags.length > 0) {
        payload.tags = tags;
      }

      const response = await api.post(
        COMMUNITIES_ENDPOINTS.SAVE_THREAD(threadId),
        payload
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Unsave thread
   * @param {string} threadId - Thread ID
   * @returns {Promise<Object>} Unsave response
   */
  unsaveThread: async (threadId) => {
    try {
      const api = createAuthAxios();
      const response = await api.delete(
        COMMUNITIES_ENDPOINTS.UNSAVE_THREAD(threadId)
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get saved threads
   * @param {string} collection - Filter by collection (optional)
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} Saved threads with pagination
   */
  getSavedThreads: async (collection = null, page = 1, limit = 20) => {
    try {
      const api = createAuthAxios();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (collection) {
        params.append("collection", collection);
      }

      const response = await api.get(
        `${COMMUNITIES_ENDPOINTS.SAVED_THREADS}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get user's save collections
   * @returns {Promise<Object>} List of collections
   */
  getCollections: async () => {
    try {
      const api = createAuthAxios();
      const response = await api.get(COMMUNITIES_ENDPOINTS.SAVED_COLLECTIONS);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Get all saved thread IDs (for local caching)
   * This is the recommended approach for instant save status checks
   * @returns {Promise<Object>} { threadIds: string[], count: number }
   */
  getSavedThreadIds: async () => {
    try {
      const api = createAuthAxios();
      const response = await api.get(COMMUNITIES_ENDPOINTS.SAVED_THREAD_IDS);
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },
};

export default communityFeedService;
